// OB Builder — admin pricing console. A developer-console / pricing-playground
// for exercising the ob-core + ob-runtime pipeline against LIVE Optimal Blue
// under an explicitly selected server-resolved identity. Form is driven by the
// fieldRules matrix (DSCR hides DTI, etc.); results surface the exact request,
// raw OB response, best-per-strategy, and every taxonomy state. Admin-only
// (matches the edge fn's requireAdmin).

import React, { useMemo, useState } from 'react';
import {
  Beaker, Play, ShieldCheck, ShieldAlert, Loader2, AlertTriangle, CheckCircle2,
  CircleSlash, Timer, DatabaseZap, Sparkles
} from 'lucide-react';
import { useAuth } from '@/contexts/useAuth';
import { runObBuilder } from '@/services/obBuilderService';
import { evaluateFieldRules } from './fieldRules';
import { JsonView } from './JsonView';
import {
  type ObScenario, type ObBuilderIdentityMode, type ObBuilderResponse,
  type EligibleProduct, type Opt,
  LOAN_PURPOSES, LOAN_TYPES, OCCUPANCIES, PROPERTY_TYPES, PRODUCT_TYPES, LOAN_TERMS,
  LIEN_TYPES, AMORTIZATION_TYPES, ARM_FIXED_TERMS, AUS_OPTIONS, BUYDOWNS, MI_OPTIONS,
  CITIZENSHIPS, UNITS, PREPAYMENT_PENALTIES, VETERAN_TYPES, INCOME_VERIFICATION_TYPES,
  HOUSING_EVENTS, EVENT_SEASONINGS, BANKRUPTCY_TYPES, BANKRUPTCY_OUTCOMES, MORTGAGE_LATES
} from './types';

type Anchor = 'price' | 'rate';

interface FieldDef {
  key: keyof ObScenario;
  label: string;
  type: 'number' | 'text' | 'select' | 'bool';
  options?: Opt[];
  ruleKey?: string; // fieldRules key when it differs from the scenario key
  pii?: boolean;
  placeholder?: string;
}

const SECTIONS: { title: string; fields: FieldDef[] }[] = [
  {
    title: 'Loan',
    fields: [
      { key: 'loanPurpose', label: 'Purpose', type: 'select', options: LOAN_PURPOSES },
      { key: 'loanType', label: 'Loan Type', type: 'select', options: LOAN_TYPES },
      { key: 'loanTermOb', label: 'Term', type: 'select', options: LOAN_TERMS },
      { key: 'baseLoanAmount', label: 'Loan Amount', type: 'number', placeholder: '400000' },
      { key: 'amortizationType', label: 'Amortization', type: 'select', options: AMORTIZATION_TYPES },
      { key: 'armFixedTermOb', label: 'ARM Fixed Period', type: 'select', options: ARM_FIXED_TERMS, ruleKey: 'armFixedTerm' },
      { key: 'interestOnly', label: 'Interest Only', type: 'bool' },
      { key: 'interestOnlyTermMonths', label: 'IO Term (mo)', type: 'number', placeholder: '120' },
      { key: 'cashOutAmount', label: 'Cash-Out Amount', type: 'number', placeholder: '50000' },
      { key: 'lienType', label: 'Lien', type: 'select', options: LIEN_TYPES },
      { key: 'firstLienAmount', label: 'First-Lien Amount', type: 'number' },
      { key: 'secondLienAmount', label: 'Subordinate 2nd', type: 'number' },
      { key: 'productTypes', label: 'Product Filters', type: 'select', options: PRODUCT_TYPES },
      { key: 'prepaymentPenalty', label: 'Prepay Penalty', type: 'select', options: PREPAYMENT_PENALTIES },
      { key: 'buydown', label: 'Buydown', type: 'select', options: BUYDOWNS },
      { key: 'aus', label: 'AUS', type: 'select', options: AUS_OPTIONS },
      { key: 'borrowerPaidMI', label: 'MI', type: 'select', options: MI_OPTIONS },
      { key: 'lockDays', label: 'Lock Days', type: 'number', placeholder: '30' }
    ]
  },
  {
    title: 'Borrower',
    fields: [
      { key: 'fico', label: 'FICO', type: 'number', placeholder: '740' },
      { key: 'dti', label: 'DTI', type: 'number', placeholder: '43' },
      { key: 'dscrRatio', label: 'DSCR Ratio', type: 'number', placeholder: '1.25' },
      { key: 'monthsReserves', label: 'Reserves (mo)', type: 'number', placeholder: '6' },
      { key: 'incomeVerificationType', label: 'Doc Type', type: 'select', options: INCOME_VERIFICATION_TYPES },
      { key: 'citizenship', label: 'Citizenship', type: 'select', options: CITIZENSHIPS },
      { key: 'veteranType', label: 'Veteran Type', type: 'select', options: VETERAN_TYPES },
      { key: 'selfEmployed', label: 'Self-Employed', type: 'bool' },
      { key: 'firstTimeHomeBuyer', label: 'First-Time Buyer', type: 'bool' },
      { key: 'waiveEscrows', label: 'Waive Escrows', type: 'bool' },
      { key: 'propertiesFinanced', label: 'Props Financed', type: 'number' },
      { key: 'borrowerFirstName', label: 'First Name', type: 'text', pii: true },
      { key: 'borrowerLastName', label: 'Last Name', type: 'text', pii: true }
    ]
  },
  {
    title: 'Property',
    fields: [
      { key: 'appraisedValue', label: 'Appraised Value', type: 'number', placeholder: '500000' },
      { key: 'salesPrice', label: 'Sales Price', type: 'number' },
      { key: 'occupancy', label: 'Occupancy', type: 'select', options: OCCUPANCIES },
      { key: 'propertyType', label: 'Property Type', type: 'select', options: PROPERTY_TYPES },
      { key: 'propertyState', label: 'State', type: 'text', placeholder: 'CA' },
      { key: 'propertyCounty', label: 'County', type: 'text', placeholder: 'Los Angeles' },
      { key: 'propertyZip', label: 'ZIP', type: 'text', placeholder: '90001' },
      { key: 'numberOfUnits', label: 'Units', type: 'select', options: UNITS },
      { key: 'numberOfStories', label: 'Stories', type: 'number', placeholder: '1' },
      { key: 'propertyCity', label: 'City', type: 'text', pii: true },
      { key: 'propertyStreetAddress', label: 'Street', type: 'text', pii: true }
    ]
  },
  {
    title: 'Expanded Guidelines',
    fields: [
      { key: 'housingEvent', label: 'Housing Event', type: 'select', options: HOUSING_EVENTS },
      { key: 'housingEventSeasoning', label: 'Event Seasoning', type: 'select', options: EVENT_SEASONINGS },
      { key: 'bankruptcy', label: 'Bankruptcy', type: 'select', options: BANKRUPTCY_TYPES },
      { key: 'bankruptcyOutcome', label: 'BK Outcome', type: 'select', options: BANKRUPTCY_OUTCOMES },
      { key: 'bankruptcySeasoning', label: 'BK Seasoning', type: 'select', options: EVENT_SEASONINGS },
      { key: 'mortgageLates', label: 'Mortgage Lates', type: 'select', options: MORTGAGE_LATES },
      { key: 'debtConsolidation', label: 'Debt Consolidation', type: 'bool' },
      { key: 'uniqueProperty', label: 'Unique Property', type: 'bool' }
    ]
  }
];

const PRESETS: { label: string; anchor: Anchor; scenario: Partial<ObScenario> }[] = [
  {
    label: 'Conventional', anchor: 'price',
    scenario: {
      loanPurpose: 'Purchase', loanType: 'Conventional', loanTermOb: 'ThirtyYear', baseLoanAmount: 400000,
      appraisedValue: 500000, fico: 740, dti: 43, propertyState: 'CA', propertyCounty: 'Los Angeles',
      propertyZip: '90001', propertyType: 'SingleFamily', occupancy: 'PrimaryResidence',
      productTypes: ['StandardProducts'], compPercent: 1.5
    }
  },
  {
    label: 'DSCR Investment', anchor: 'price',
    scenario: {
      loanPurpose: 'RefiCashout', loanType: 'NonConforming', loanTermOb: 'ThirtyYear', baseLoanAmount: 300000,
      appraisedValue: 400000, fico: 720, cashOutAmount: 50000, propertyState: 'TX', propertyCounty: 'Harris',
      propertyZip: '77001', propertyType: 'SingleFamily', occupancy: 'InvestmentProperty',
      productTypes: ['ExpandedGuidelines'], incomeVerificationType: 'InvestorDscr', dscrRatio: 1.25,
      monthsReserves: 6, compPercent: 2.0
    }
  },
  {
    label: 'VA', anchor: 'rate',
    scenario: {
      loanPurpose: 'VARateReduction', loanType: 'VA', loanTermOb: 'ThirtyYear', baseLoanAmount: 300000,
      appraisedValue: 350000, fico: 680, dti: 41, propertyState: 'VA', propertyCounty: 'Fairfax',
      propertyZip: '22101', propertyType: 'SingleFamily', occupancy: 'PrimaryResidence',
      productTypes: ['StandardProducts'], veteranType: 'ActiveDuty', targetRate: 6.5
    }
  }
];

const STATUS_META: Record<string, { label: string; tone: string; Icon: React.ComponentType<{ className?: string }> }> = {
  priced: { label: 'Priced', tone: 'text-emerald-700 bg-emerald-50 border-emerald-200 dark:text-emerald-300 dark:bg-emerald-950/40 dark:border-emerald-900', Icon: CheckCircle2 },
  validated: { label: 'Validated', tone: 'text-sky-700 bg-sky-50 border-sky-200 dark:text-sky-300 dark:bg-sky-950/40 dark:border-sky-900', Icon: ShieldCheck },
  no_products: { label: 'No Products', tone: 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-900', Icon: CircleSlash },
  products_without_priceable_values: { label: 'No Priceable Values', tone: 'text-amber-700 bg-amber-50 border-amber-200 dark:text-amber-300 dark:bg-amber-950/40 dark:border-amber-900', Icon: CircleSlash },
  validation_error: { label: 'Validation Error', tone: 'text-rose-700 bg-rose-50 border-rose-200 dark:text-rose-300 dark:bg-rose-950/40 dark:border-rose-900', Icon: AlertTriangle },
  ob_error: { label: 'OB Error', tone: 'text-rose-700 bg-rose-50 border-rose-200 dark:text-rose-300 dark:bg-rose-950/40 dark:border-rose-900', Icon: AlertTriangle },
  ob_timeout: { label: 'OB Timeout', tone: 'text-rose-700 bg-rose-50 border-rose-200 dark:text-rose-300 dark:bg-rose-950/40 dark:border-rose-900', Icon: Timer },
  ob_parse_error: { label: 'Parse Error', tone: 'text-rose-700 bg-rose-50 border-rose-200 dark:text-rose-300 dark:bg-rose-950/40 dark:border-rose-900', Icon: AlertTriangle },
  rate_limited: { label: 'Rate Limited', tone: 'text-orange-700 bg-orange-50 border-orange-200 dark:text-orange-300 dark:bg-orange-950/40 dark:border-orange-900', Icon: Timer }
};

const money = (n?: number | null) => (typeof n === 'number' ? n.toLocaleString('en-US', { maximumFractionDigits: 0 }) : '—');
const rate = (n?: number | null) => (typeof n === 'number' ? `${n.toFixed(3)}%` : '—');
const price = (n?: number | null) => (typeof n === 'number' ? n.toFixed(3) : '—');

export function OBBuilder() {
  const { user } = useAuth();
  const isAdmin = (user?.roles ?? []).some((r) => String(r).toLowerCase() === 'admin');

  const [scenario, setScenario] = useState<Partial<ObScenario>>(PRESETS[0].scenario);
  const [anchor, setAnchor] = useState<Anchor>('price');
  const [identityMode, setIdentityMode] = useState<ObBuilderIdentityMode>('admin_actor');
  const [includePii, setIncludePii] = useState(false);
  const [busy, setBusy] = useState<null | 'validate' | 'live'>(null);
  const [result, setResult] = useState<ObBuilderResponse | null>(null);
  const [runErr, setRunErr] = useState<string | null>(null);
  const [tab, setTab] = useState<'request' | 'response' | 'best' | 'products'>('request');

  const rules = useMemo(() => evaluateFieldRules({
    loanType: scenario.loanType, loanPurpose: scenario.loanPurpose, productTypes: scenario.productTypes,
    incomeVerificationType: scenario.incomeVerificationType, lienType: scenario.lienType,
    amortizationType: scenario.amortizationType, interestOnly: scenario.interestOnly,
    occupancy: scenario.occupancy, prepaymentPenalty: scenario.prepaymentPenalty
  }), [scenario]);
  const hidden = useMemo(() => new Set(rules.hidden), [rules]);
  const required = useMemo(() => new Set(rules.required), [rules]);

  if (!isAdmin) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="max-w-md rounded-xl border border-rose-200 bg-rose-50 p-6 text-center dark:border-rose-900 dark:bg-rose-950/40">
          <ShieldAlert className="mx-auto mb-3 h-8 w-8 text-rose-600 dark:text-rose-400" />
          <div className="text-sm font-semibold text-rose-800 dark:text-rose-200">Admin access required</div>
          <div className="mt-1 text-xs text-rose-600/80 dark:text-rose-300/70">The OB Builder is an admin-only pricing console.</div>
        </div>
      </div>
    );
  }

  const setField = (key: keyof ObScenario, value: unknown) =>
    setScenario((s) => ({ ...s, [key]: value }));

  const applyPreset = (p: typeof PRESETS[number]) => {
    setScenario(p.scenario);
    setAnchor(p.anchor);
    setResult(null);
    setRunErr(null);
  };

  // Send ONLY what's visible: strip fields hidden by fieldRules, PII when off, and
  // the non-active anchor input — so stale hidden values can't trip validation for
  // fields the admin can't see. (Stripping dti for DSCR is fine: ob-core re-applies
  // the decided fallback of 43.)
  const submitScenario = (): Partial<ObScenario> => {
    const out: Partial<ObScenario> = { ...scenario };
    if (anchor === 'price') {
delete out.targetRate;
} else {
delete out.compPercent;
}
    if (!includePii) {
      for (const k of ['borrowerFirstName', 'borrowerLastName', 'borrowerStreetAddress', 'propertyCity', 'propertyStreetAddress'] as (keyof ObScenario)[]) {
        delete out[k];
      }
    }
    for (const rk of hidden) {
      const key = (rk === 'armFixedTerm' ? 'armFixedTermOb' : rk) as keyof ObScenario;
      delete out[key];
    }
    return out;
  };

  const run = async (mode: 'validate' | 'live') => {
    setBusy(mode);
    setRunErr(null);
    try {
      const res = await runObBuilder({
        mode,
        anchor,
        identityMode,
        includePii,
        scenario: submitScenario()
      });
      setResult(res);
      setTab(mode === 'live' && res.status !== 'validation_error' ? 'response' : 'request');
    } catch (e) {
      setRunErr(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setBusy(null);
    }
  };

  const status = result?.status;
  const meta = status ? STATUS_META[status] : null;

  return (
    <div className="flex h-full flex-col overflow-hidden bg-zinc-50 dark:bg-zinc-950">
      {/* Header */}
      <header className="relative shrink-0 border-b border-zinc-200 bg-white px-6 py-4 dark:border-zinc-800 dark:bg-zinc-900">
        <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-violet-500 via-indigo-500 to-sky-500" />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 text-white shadow-sm">
              <Beaker className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-base font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">OB Builder</h1>
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                Optimal Blue pricing playground · {
                  identityMode === 'admin_actor'
                    ? 'your linked OB identity'
                    : 'managed company identity'
                } · <span className="font-mono">ob-core</span> engine
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2.5 py-1 font-medium text-zinc-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Admin
            </span>
          </div>
        </div>
      </header>

      {/* Body: split panel */}
      <div className="grid min-h-0 flex-1 gap-4 overflow-hidden p-4 lg:grid-cols-[minmax(0,440px)_minmax(0,1fr)]">
        {/* LEFT — scenario editor */}
        <aside className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
          {/* Run bar */}
          <div className="shrink-0 space-y-3 border-b border-zinc-100 p-4 dark:border-zinc-800">
            <div>
              <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-zinc-400">
                Pricing identity
              </div>
              <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-zinc-200 text-xs font-medium dark:border-zinc-700">
                {([
                  ['admin_actor', 'My linked OB user'],
                  ['company', 'Managed company seat']
                ] as const).map(([mode, label]) => (
                  <button
                    key={mode}
                    type="button"
                    onClick={() => {
                      setIdentityMode(mode);
                      setResult(null);
                      setRunErr(null);
                    }}
                    className={`px-3 py-1.5 transition ${
                      identityMode === mode
                        ? 'bg-indigo-600 text-white'
                        : 'bg-white text-zinc-600 hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="mt-1.5 text-[10px] leading-relaxed text-zinc-400">
                IDs are resolved on the server. The browser cannot supply an OB user or business channel.
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="inline-flex overflow-hidden rounded-lg border border-zinc-200 text-xs font-medium dark:border-zinc-700">
                {(['price', 'rate'] as Anchor[]).map((a) => (
                  <button
                    key={a}
                    onClick={() => setAnchor(a)}
                    className={`px-3 py-1.5 transition ${anchor === a ? 'bg-indigo-600 text-white' : 'bg-white text-zinc-600 hover:bg-zinc-50 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800'}`}
                  >
                    {a === 'price' ? 'Price-anchored' : 'Rate-anchored'}
                  </button>
                ))}
              </div>
              <label className="ml-auto inline-flex cursor-pointer items-center gap-1.5 text-xs text-zinc-600 dark:text-zinc-300">
                <input type="checkbox" checked={includePii} onChange={(e) => setIncludePii(e.target.checked)} className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600" />
                Include PII
              </label>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => run('validate')}
                disabled={busy !== null}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-3 py-2 text-xs font-semibold text-zinc-700 transition hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-200 dark:hover:bg-zinc-700"
              >
                {busy === 'validate' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5" />}
                Validate
              </button>
              <button
                onClick={() => run('live')}
                disabled={busy !== null}
                className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:from-violet-500 hover:to-indigo-500 disabled:opacity-50"
              >
                {busy === 'live' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
                Live Run
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-zinc-400"><Sparkles className="h-3 w-3" /> Presets</span>
              {PRESETS.map((p) => (
                <button key={p.label} onClick={() => applyPreset(p)} className="rounded-md border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[11px] text-zinc-600 transition hover:border-indigo-300 hover:text-indigo-600 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300">
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-4">
            {/* anchor input */}
            <div className="grid grid-cols-2 gap-2.5">
              {anchor === 'price' ? (
                <NumField label="Comp %" required value={scenario.compPercent} onChange={(v) => setField('compPercent', v)} placeholder="1.5" />
              ) : (
                <NumField label="Target Rate" required value={scenario.targetRate} onChange={(v) => setField('targetRate', v)} placeholder="6.5" />
              )}
            </div>

            {SECTIONS.map((section) => {
              const visible = section.fields.filter((f) => {
                if (f.pii && !includePii) {
return false;
}
                return !hidden.has(f.ruleKey ?? (f.key as string));
              });
              if (visible.length === 0) {
return null;
}
              return (
                <div key={section.title}>
                  <div className="mb-2 flex items-center gap-2">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">{section.title}</h3>
                    <div className="h-px flex-1 bg-zinc-100 dark:bg-zinc-800" />
                  </div>
                  <div className="grid grid-cols-2 gap-2.5">
                    {visible.map((f) => {
                      const req = required.has(f.ruleKey ?? (f.key as string));
                      if (f.type === 'bool') {
                        return (
                          <label key={f.key as string} className="col-span-2 flex cursor-pointer items-center gap-2 rounded-lg border border-zinc-100 px-2.5 py-1.5 text-xs text-zinc-600 transition hover:border-zinc-200 dark:border-zinc-800 dark:text-zinc-300 dark:hover:border-zinc-700 sm:col-span-1">
                            <input type="checkbox" checked={Boolean(scenario[f.key])} onChange={(e) => setField(f.key, e.target.checked)} className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600" />
                            {f.label}
                          </label>
                        );
                      }
                      if (f.type === 'select') {
                        return (
                          <SelectField
                            key={f.key as string}
                            label={f.label} required={req}
                            value={f.key === 'productTypes' ? (scenario.productTypes?.[0] ?? '') : ((scenario[f.key] as string) ?? '')}
                            options={f.options ?? []}
                            onChange={(v) => setField(f.key, f.key === 'productTypes' ? (v ? [v] : undefined) : (v || undefined))}
                          />
                        );
                      }
                      if (f.type === 'number') {
                        return (
                          <NumField key={f.key as string} label={f.label} required={req} placeholder={f.placeholder}
                            value={scenario[f.key] as number | undefined}
                            onChange={(v) => setField(f.key, v)} />
                        );
                      }
                      return (
                        <TextField key={f.key as string} label={f.label} required={req} placeholder={f.placeholder}
                          value={(scenario[f.key] as string) ?? ''}
                          onChange={(v) => setField(f.key, v || undefined)} />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </aside>

        {/* RIGHT — results */}
        <main className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white dark:border-zinc-800 dark:bg-zinc-900">
          {/* Status bar */}
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-zinc-100 p-3 dark:border-zinc-800">
            {meta ? (
              <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${meta.tone}`}>
                <meta.Icon className="h-3.5 w-3.5" /> {meta.label}
              </span>
            ) : (
              <span className="text-xs text-zinc-400">Run a scenario to see results →</span>
            )}
            {result?.cached && (
              <span className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 text-[10px] font-medium text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800"><DatabaseZap className="h-3 w-3" /> cached</span>
            )}
            {typeof result?.durationMs === 'number' && (
              <span className="inline-flex items-center gap-1 text-[11px] text-zinc-400"><Timer className="h-3 w-3" /> {result.durationMs}ms</span>
            )}
            {result?.response?.searchId && (
              <span className="inline-flex items-center gap-1 font-mono text-[11px] text-zinc-400">search {result.response.searchId.slice(0, 10)}…</span>
            )}
            {result?.pricingIdentity && (
              <span className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-zinc-50 px-2 py-0.5 font-mono text-[10px] text-zinc-500 dark:border-zinc-700 dark:bg-zinc-800">
                {result.pricingIdentity.mode === 'admin_actor' ? 'admin actor' : 'company'}
                {' · '}
                {result.pricingIdentity.businessChannelId}/{result.pricingIdentity.originatorId}
              </span>
            )}
            {result && result.persisted === false && (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">not persisted</span>
            )}
          </div>

          {/* Transport / validation errors */}
          {runErr && (
            <div className="m-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{runErr}</span>
            </div>
          )}
          {result?.message && result.status === 'rate_limited' && (
            <div className="m-3 flex items-start gap-2 rounded-lg border border-orange-200 bg-orange-50 p-3 text-xs text-orange-700 dark:border-orange-900 dark:bg-orange-950/40 dark:text-orange-300">
              <Timer className="mt-0.5 h-4 w-4 shrink-0" /> <span>{result.message}</span>
            </div>
          )}
          {result?.errors && result.errors.length > 0 && (
            <div className="m-3 rounded-lg border border-rose-200 bg-rose-50 p-3 dark:border-rose-900 dark:bg-rose-950/40">
              <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-rose-600 dark:text-rose-300">Validation ({result.errors.length})</div>
              <ul className="space-y-0.5 text-xs text-rose-700 dark:text-rose-300">
                {result.errors.map((e, i) => <li key={i} className="flex gap-1.5"><span className="text-rose-400">•</span>{e}</li>)}
              </ul>
            </div>
          )}
          {result?.error?.message && result.status !== 'validation_error' && (
            <div className="m-3 flex items-start gap-2 rounded-lg border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> <span>{result.error.message}</span>
            </div>
          )}

          {/* Tabs */}
          <div className="flex shrink-0 gap-1 border-b border-zinc-100 px-3 pt-1 dark:border-zinc-800">
            {([
              ['request', 'Request'],
              ['response', 'Raw Response'],
              ['best', 'Best Picks'],
              ['products', `Products${result?.products?.length ? ` (${result.products.length})` : ''}`]
            ] as const).map(([id, label]) => (
              <button
                key={id}
                onClick={() => setTab(id)}
                className={`relative px-3 py-2 text-xs font-medium transition ${tab === id ? 'text-indigo-600 dark:text-indigo-400' : 'text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200'}`}
              >
                {label}
                {tab === id && <span className="absolute inset-x-2 -bottom-px h-0.5 rounded-full bg-indigo-500" />}
              </button>
            ))}
          </div>

          {/* Panel content */}
          <div className="min-h-0 flex-1 overflow-y-auto p-4">
            {tab === 'request' && <JsonView value={result?.request ?? null} empty="Run to see the exact OB request payload." maxHeight="100%" />}
            {tab === 'response' && <JsonView value={result?.response?.raw ?? null} empty="No raw OB response yet (validate mode makes no OB call)." maxHeight="100%" />}
            {tab === 'best' && <BestPicks best={result?.best ?? null} />}
            {tab === 'products' && <ProductsTable products={result?.products ?? []} />}
          </div>

          {/* fieldRules notes */}
          {result?.fieldRules?.notes && result.fieldRules.notes.length > 0 && (
            <div className="shrink-0 border-t border-zinc-100 bg-zinc-50/60 px-4 py-2 dark:border-zinc-800 dark:bg-zinc-950/40">
              {result.fieldRules.notes.map((n, i) => (
                <div key={i} className="flex items-center gap-1.5 text-[11px] text-zinc-500"><Beaker className="h-3 w-3 text-indigo-400" /> {n}</div>
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// ── field primitives ─────────────────────────────────────────────────────────
function FieldShell({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium text-zinc-500 dark:text-zinc-400">
        {label}{required && <span className="ml-0.5 text-rose-500">*</span>}
      </span>
      {children}
    </label>
  );
}
const inputCls = 'w-full rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5 text-sm text-zinc-800 outline-none transition focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-100 dark:focus:ring-indigo-900/40';

function NumField({ label, required, value, onChange, placeholder }: { label: string; required?: boolean; value?: number; onChange: (v: number | undefined) => void; placeholder?: string }) {
  return (
    <FieldShell label={label} required={required}>
      <input type="number" className={inputCls} placeholder={placeholder} value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? undefined : Number(e.target.value))} />
    </FieldShell>
  );
}
function TextField({ label, required, value, onChange, placeholder }: { label: string; required?: boolean; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <FieldShell label={label} required={required}>
      <input type="text" className={inputCls} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </FieldShell>
  );
}
function SelectField({ label, required, value, options, onChange }: { label: string; required?: boolean; value: string; options: Opt[]; onChange: (v: string) => void }) {
  return (
    <FieldShell label={label} required={required}>
      <select className={inputCls} value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </FieldShell>
  );
}

// ── result views ─────────────────────────────────────────────────────────────
function BestCard({ title, product, accent }: { title: string; product: EligibleProduct | null; accent: string }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4 dark:border-zinc-800 dark:bg-zinc-900">
      <div className={`mb-3 text-[11px] font-semibold uppercase tracking-wider ${accent}`}>{title}</div>
      {product ? (
        <div className="space-y-2">
          <div className="flex items-baseline gap-3">
            <div>
              <div className="text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-50">{rate(product.rate)}</div>
              <div className="text-[10px] uppercase text-zinc-400">rate</div>
            </div>
            <div>
              <div className="text-2xl font-bold tabular-nums text-zinc-900 dark:text-zinc-50">{price(product.price)}</div>
              <div className="text-[10px] uppercase text-zinc-400">price</div>
            </div>
          </div>
          <div className="truncate text-sm font-medium text-zinc-700 dark:text-zinc-200" title={product.productName}>{product.productName ?? '—'}</div>
          <div className="text-xs text-zinc-500">{product.investor ?? '—'}</div>
          <div className="flex gap-4 pt-1 text-[11px] text-zinc-400">
            <span>P&amp;I ${money(product.principalAndInterest)}</span>
            <span>Lock {product.lockPeriod ?? '—'}d</span>
            {typeof product.monthlyMI === 'number' && product.monthlyMI > 0 && <span>MI ${money(product.monthlyMI)}</span>}
          </div>
        </div>
      ) : (
        <div className="py-6 text-center text-xs text-zinc-400">No qualifying product</div>
      )}
    </div>
  );
}
function BestPicks({ best }: { best: { byRateBucket: EligibleProduct | null; byPriceFloor: EligibleProduct | null } | null }) {
  if (!best) {
return <div className="py-8 text-center text-xs text-zinc-400">Run a live pricing to compare strategies.</div>;
}
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <BestCard title="Price-Floor (lo-preview)" product={best.byPriceFloor} accent="text-sky-500" />
      <BestCard title="Rate-Bucket (Artemis)" product={best.byRateBucket} accent="text-violet-500" />
    </div>
  );
}
function ProductsTable({ products }: { products: EligibleProduct[] }) {
  if (products.length === 0) {
return <div className="py-8 text-center text-xs text-zinc-400">No eligible products.</div>;
}
  return (
    <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-zinc-800">
      <table className="min-w-full text-left text-xs">
        <thead className="bg-zinc-50 text-[10px] uppercase tracking-wide text-zinc-400 dark:bg-zinc-800/60">
          <tr>
            <th className="px-3 py-2 font-medium">Product</th>
            <th className="px-3 py-2 font-medium">Investor</th>
            <th className="px-3 py-2 text-right font-medium">Rate</th>
            <th className="px-3 py-2 text-right font-medium">Price</th>
            <th className="px-3 py-2 text-right font-medium">P&amp;I</th>
            <th className="px-3 py-2 text-right font-medium">Lock</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100 dark:divide-zinc-800">
          {products.map((p, i) => (
            <tr key={i} className="text-zinc-700 hover:bg-zinc-50 dark:text-zinc-200 dark:hover:bg-zinc-800/40">
              <td className="max-w-[16rem] truncate px-3 py-1.5" title={p.productName}>{p.productName ?? '—'}</td>
              <td className="max-w-[10rem] truncate px-3 py-1.5 text-zinc-500">{p.investor ?? '—'}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{rate(p.rate)}</td>
              <td className="px-3 py-1.5 text-right tabular-nums">{price(p.price)}</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-zinc-500">${money(p.principalAndInterest)}</td>
              <td className="px-3 py-1.5 text-right tabular-nums text-zinc-500">{p.lockPeriod ?? '—'}d</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
